import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { AnimatedLoginBackground } from '@/components/layout/AnimatedLoginBackground';
import { Logo } from '@/components/ui/Logo';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { createStripeCheckoutSession } from '@/lib/stripePlans';

export const AuthCallback = () => {
    const navigate = useNavigate();
    const isRedirectingRef = useRef(false);
    
    // Check URL for error parameters on mount
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const errorParam = params.get('error') || hashParams.get('error');
    const errorDescription = params.get('error_description') || hashParams.get('error_description');
    
    const initialError = errorParam === 'access_denied' 
        ? 'Você cancelou o login. Tente novamente quando quiser.' 
        : errorParam 
            ? (errorDescription || 'Ocorreu um erro durante a autenticação. Tente novamente.') 
            : null;

    const [error, setError] = useState<string | null>(initialError);

    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, []);

    const processRedirect = useCallback(async () => {
        if (isRedirectingRef.current) return;
        isRedirectingRef.current = true;

        const pendingPlanRaw = sessionStorage.getItem('pending_checkout_plan');
        if (pendingPlanRaw) {
            try {
                sessionStorage.removeItem('pending_checkout_plan');
                const pending = JSON.parse(pendingPlanRaw);
                if (pending?.priceId) {
                    const checkoutUrl = await createStripeCheckoutSession(pending.priceId);
                    if (checkoutUrl) {
                        window.location.href = checkoutUrl;
                        return;
                    }
                }
            } catch (checkoutErr) {
                console.error('Erro ao redirecionar para checkout no callback OAuth:', checkoutErr);
            }
        }
        navigate('/dashboard', { replace: true });
    }, [navigate]);

    useEffect(() => {
        // Supabase automatically processes the hash fragment with the tokens
        // We just listen for the auth state change
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
            if (event === 'SIGNED_IN') {
                setTimeout(processRedirect, 300);
            }
        });

        // Check if user is already signed in (fast redirect)
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session) {
                processRedirect();
            }
        });

        // If nothing happens after 15s, show error
        const timeout = setTimeout(() => {
            if (!isRedirectingRef.current) {
                setError('A autenticação demorou mais do que o esperado. Tente novamente.');
            }
        }, 15000);

        return () => {
            subscription.unsubscribe();
            clearTimeout(timeout);
        };
    }, [navigate, processRedirect]);

    return (
        <AnimatedLoginBackground>
            <div className="flex flex-col items-center justify-center w-full h-full p-4">
                <div className="w-full max-w-[420px] bg-white/95 backdrop-blur-sm rounded-[1.5rem] shadow-2xl border border-slate-100/80 overflow-hidden animate-form-fade-in">
                    <div className="p-6 sm:p-8 flex flex-col items-center justify-center">
                        <Logo theme="light" size="large" className="mb-6" />
                        
                        {error ? (
                            <div className="w-full space-y-4 animate-in fade-in duration-300">
                                <div className="flex items-start gap-3 p-4 bg-red-50 text-red-700 rounded-xl border border-red-100">
                                    <AlertCircle size={20} className="shrink-0 mt-0.5" />
                                    <p className="text-sm font-medium">{error}</p>
                                </div>
                                <Button
                                    fullWidth
                                    onClick={() => navigate('/login', { replace: true })}
                                    className="py-2.5 text-sm font-bold shadow-lg shadow-indigo-600/20 rounded-xl"
                                >
                                    Voltar para o Login
                                </Button>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-5 py-4">
                                <div className="relative flex items-center justify-center w-16 h-16">
                                    <div className="absolute inset-0 border-[4px] border-indigo-100 rounded-full"></div>
                                    <div className="absolute inset-0 border-[4px] border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                                </div>
                                <p className="text-[14px] font-bold text-indigo-700 animate-pulse">
                                    Conectando sua conta...
                                </p>
                                <p className="text-xs text-slate-500 font-medium text-center">
                                    Estamos finalizando a autenticação. Você será redirecionado em instantes.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AnimatedLoginBackground>
    );
};
